import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { CheckCircle2, Loader2, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AppointmentFields, EMPTY_FORM, validateForm } from "@/components/AppointmentFields";
import { SundayBanner } from "@/components/site/SundayBanner";
import { api, formatApiError, formatTime } from "@/lib/api";
import { CLINIC, whatsappLink, whatsappAppLink } from "@/constants/clinic";

const prettyDate = (d) => format(new Date(`${d}T00:00:00`), "EEE, d MMM yyyy");

const whatsappText = (b) =>
  `*New Appointment Booking*\nRef: ${b.reference}\nPatient: ${b.name} (${b.age}, ${b.gender}) · +91 ${b.phone}\n` +
  `${b.clinic === "online" ? "Online Consultation" : `In-Clinic · ${b.clinic_name}`}\n${prettyDate(b.date)} at ${formatTime(b.time)}\nDoctor: ${CLINIC.doctor}`;

const IS_MOBILE = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const openWhatsApp = (booking, desktopWindow) => {
  const text = whatsappText(booking);
  if (IS_MOBILE) window.location.href = whatsappAppLink(text);
  else if (desktopWindow) desktopWindow.location.href = whatsappLink(text);
};

// Desktop browsers block pop-ups opened after a delay, so reserve the tab during the click itself.
const reserveDesktopWindow = () => {
  if (IS_MOBILE) return null;
  const win = window.open("", "_blank");
  if (win) win.opener = null;
  return win;
};

const Success = ({ booking, onReset }) => {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);
  return (
  <div ref={ref} className="text-center py-10" data-testid="booking-success">
    <CheckCircle2 className="h-14 w-14 text-secondary mx-auto" />
    <h3 className="mt-5 text-2xl font-semibold text-[#1A252C]">Booking confirmed</h3>
    <p className="mt-3 text-muted-foreground">
      {booking.name}, your {booking.clinic === "online" ? "online consultation" : <>visit at <b>{booking.clinic_name}</b></>} on <b>{prettyDate(booking.date)}</b> at <b>{formatTime(booking.time)}</b> has been received.
    </p>
    <p className="mt-6 inline-block rounded-xl bg-accent px-5 py-3 font-display font-bold text-primary text-lg tracking-wider" data-testid="booking-reference">{booking.reference}</p>
    <p className="mt-4 text-sm text-muted-foreground max-w-md mx-auto" data-testid="booking-whatsapp-note">WhatsApp opens automatically with your booking details. Just tap <b>Send</b>. If it didn't open, tap the green button below.</p>
    <div className="mt-6 flex flex-wrap justify-center gap-3">
      <Button asChild className="rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white" data-testid="booking-whatsapp-btn">
        <a href={whatsappLink(whatsappText(booking))} target="_blank" rel="noreferrer"><MessageCircle className="mr-2 h-4 w-4" /> Send to WhatsApp</a>
      </Button>
      <Button variant="outline" className="rounded-full" onClick={onReset} data-testid="booking-new-btn">Book another</Button>
    </div>
  </div>
  );
};

export const Booking = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    const err = validateForm(form);
    if (err) return toast.error(err);
    setLoading(true);
    const desktopWindow = reserveDesktopWindow();
    try {
      const { data } = await api.post("/appointments", { ...form, age: Number(form.age) });
      setBooking(data);
      toast.success("Appointment booked");
      if (IS_MOBILE) setTimeout(() => openWhatsApp(data), 800);
      else openWhatsApp(data, desktopWindow);
    } catch (error) {
      desktopWindow?.close();
      toast.error(formatApiError(error));
      setRefreshKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <p className="text-sm font-semibold uppercase tracking-widest text-secondary">Appointments</p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1A252C]">Book your consultation</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">Choose an in-clinic visit or online consultation, then pick a date within the next 14 days and a 15-minute slot.</p>
          <a href={`tel:+91${CLINIC.phone}`} className="mt-6 inline-flex items-center gap-2 font-semibold text-primary hover:underline" data-testid="booking-call-link">
            <Phone className="h-4 w-4" /> Book by phone: +91 {CLINIC.phone}
          </a>
          <SundayBanner className="mt-8" />
        </div>
        <div className="lg:col-span-8 bg-white rounded-3xl border border-slate-100 shadow-sm p-6 sm:p-10" data-testid="booking-card">
          {booking ? (
            <Success booking={booking} onReset={() => { setBooking(null); setForm(EMPTY_FORM); }} />
          ) : (
            <form onSubmit={submit} data-testid="booking-form">
              <AppointmentFields form={form} setForm={setForm} prefix="booking" refreshKey={refreshKey} />
              <Button type="submit" size="lg" className="mt-8 w-full sm:w-auto rounded-full px-10 h-12" disabled={loading} data-testid="booking-submit-btn">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirm & Book
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
